import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import FilterBar from '../../components/settings/FilterBar';
import language from '../../variables/language';

export default function GroupAttendanceTimeline({ groupId, refresh, onChange }) {
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    axios.get('/api/lessons?groupe=' + groupId, { withCredentials: true })
      .then(response => setLessons(response.data || [])).catch(() => {});
    axios.get('/api/students?groupe=' + groupId, { withCredentials: true })
      .then(response => setStudents(response.data || [])).catch(() => {});
    axios.get('/api/events?groupe=' + groupId, { withCredentials: true })
      .then(response => setEvents(response.data || [])).catch(() => {});
  }, [groupId, refresh]);

  const visibleLessons = useMemo(() => {
    return lessons.filter(lesson => {
      if (dateFrom && lesson.date && lesson.date < dateFrom) return false;
      if (dateTo && lesson.date && lesson.date > dateTo) return false;
      return true;
    });
  }, [lessons, dateFrom, dateTo]);

  const eventMap = useMemo(() => {
    const map = {};
    events.forEach(event => {
      if (event.student && event.lesson) {
        map[event.student.id + '_' + event.lesson.id] = event;
      }
    });
    return map;
  }, [events]);

  const toggleAttendance = (event) => {
    const next = event.attendence === 1 ? 2 : 1;
    axios.put('/api/events/' + event.id, JSON.stringify({ attendence: next }), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then(() => onChange()).catch(() => {});
  };

  const lang = language[language.lang];

  return (
    <div className="group-card-section">
      <div className="group-attendance-header">
        <h4 className="group-card-section-title">{lang.groupCard.attendanceTitle}</h4>
        <FilterBar
          filters={[
            { key: 'dateFrom', type: 'date', label: lang.dateFrom, value: dateFrom, onChange: setDateFrom },
            { key: 'dateTo', type: 'date', label: lang.dateTo, value: dateTo, onChange: setDateTo },
          ]}
        />
      </div>
      <div className="group-attendance-legend">
        <span className="group-attendance-legend-item">
          <span className="group-attendance-dot present"></span>{lang.groupCard.attendancePresent}
        </span>
        <span className="group-attendance-legend-item">
          <span className="group-attendance-dot absent"></span>{lang.groupCard.attendanceAbsent}
        </span>
        <span className="group-attendance-legend-item">
          <span className="group-attendance-dot unmarked"></span>{lang.groupCard.attendanceUnmarked}
        </span>
      </div>
      {visibleLessons.length === 0 || students.length === 0 ? (
        <p className="group-attendance-empty">{lang.groupCard.noLessons}</p>
      ) : (
        <div className="group-attendance-scroll">
          <table className="group-attendance-grid">
            <thead>
              <tr>
                <th className="group-attendance-student-col"></th>
                {visibleLessons.map(lesson => (
                  <th className="group-attendance-lesson-head" key={lesson.id}>
                    {lesson.date ? DateTime.fromISO(lesson.date).toFormat('dd.MM') : '-'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map(student => (
                <tr key={student.id}>
                  <td className="group-attendance-student-col">{student.name} {student.surname}</td>
                  {visibleLessons.map(lesson => {
                    const event = eventMap[student.id + '_' + lesson.id];
                    const state = !event
                      ? 'none'
                      : event.attendence === 1 ? 'present' : event.attendence === 2 ? 'absent' : 'unmarked';
                    return (
                      <td key={lesson.id} className={event ? 'group-attendance-cell' : ''}
                        onClick={() => event && toggleAttendance(event)}>
                        <span className={'group-attendance-cell-dot ' + state}></span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
